// src/pages/maintenance/WorkOrderBoardPage.js
// Work order list/board — filter by status/type/priority, create new, open detail.
// See docs/MAINTENANCE_MODULE.md.
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import maintenanceService from '../../services/maintenanceService';
import SearchableSelect from '../../components/SearchableSelect';
import MaintenanceNav from '../../components/MaintenanceNav';
import WorkOrderModal from './WorkOrderModal';
import QuickCompleteWorkOrderModal from './QuickCompleteWorkOrderModal';

const STATUS_BADGE = { open: 'secondary', in_progress: 'primary', on_hold: 'warning', completed: 'success', cancelled: 'dark' };
const PRIORITY_BADGE = { low: 'secondary', medium: 'info', high: 'warning', urgent: 'danger' };
const CATEGORY_OPTIONS = [
  'oven', 'freezer', 'refrigerator', 'mixer', 'conveyor', 'compressor',
  'packaging_machine', 'scale', 'metal_detector', 'thermometer', 'other',
];
const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function WorkOrderBoardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [workOrders, setWorkOrders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', type: '', priority: '', assetId: '', category: '', search: '' });
  const [showModal, setShowModal] = useState(searchParams.get('new') === '1' || !!searchParams.get('assetId'));
  const [quickCompleteTarget, setQuickCompleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const data = await maintenanceService.getWorkOrders(params);
      setWorkOrders(data);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { maintenanceService.getAssets().then(setAssets).catch(() => setAssets([])); }, []);

  const handleSave = (wo) => {
    setShowModal(false);
    navigate(`/maintenance/work-orders/${wo.id}`);
  };

  // Mirrors WorkOrderDetailPage.js's own Quick Complete gating — a calibration WO always needs the
  // full signed-cert flow, never this shortcut.
  const isQuickCompleteEligible = (wo) =>
    ['open', 'in_progress', 'on_hold'].includes(wo.status) && wo.type === 'preventive' && !wo.isCalibrationWo;

  return (
    <>
      <MaintenanceNav />
      <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Work Orders</h4>
        <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ New Work Order</button>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <input className="form-control form-control-sm" placeholder="Search title or WO #…" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
        </div>
        <div className="col-md-3">
          <SearchableSelect
            options={['open', 'in_progress', 'on_hold', 'completed', 'cancelled'].map(s => ({ value: s, label: labelize(s) }))}
            value={filters.status}
            onChange={val => setFilters(f => ({ ...f, status: val || '' }))}
            placeholder="All statuses"
            size="sm"
          />
        </div>
        <div className="col-md-3">
          <SearchableSelect
            options={['reactive', 'preventive', 'inspection', 'calibration', 'safety'].map(t => ({ value: t, label: labelize(t) }))}
            value={filters.type}
            onChange={val => setFilters(f => ({ ...f, type: val || '' }))}
            placeholder="All types"
            size="sm"
          />
        </div>
        <div className="col-md-3">
          <SearchableSelect
            options={['low', 'medium', 'high', 'urgent'].map(p => ({ value: p, label: labelize(p) }))}
            value={filters.priority}
            onChange={val => setFilters(f => ({ ...f, priority: val || '' }))}
            placeholder="All priorities"
            size="sm"
          />
        </div>
        <div className="col-md-3">
          <SearchableSelect
            options={assets.map(a => ({ value: a.id, label: a.name }))}
            value={filters.assetId}
            onChange={val => setFilters(f => ({ ...f, assetId: val || '' }))}
            placeholder="All assets"
            size="sm"
          />
        </div>
        <div className="col-md-3">
          <SearchableSelect
            options={CATEGORY_OPTIONS.map(c => ({ value: c, label: labelize(c) }))}
            value={filters.category}
            onChange={val => setFilters(f => ({ ...f, category: val || '' }))}
            placeholder="All categories"
            size="sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5 text-muted">Loading…</div>
      ) : workOrders.length === 0 ? (
        <div className="text-center py-5 text-muted">No work orders found.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th>WO #</th><th>Title</th><th>Asset</th><th>Category</th><th>Type</th><th>Priority</th><th>Status</th><th>Due</th><th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {workOrders.map(wo => (
                <tr key={wo.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/maintenance/work-orders/${wo.id}`)}>
                  <td style={{ fontSize: 13, fontWeight: 600 }}>{wo.woNumber}</td>
                  <td style={{ fontSize: 13 }}>{wo.title}</td>
                  <td style={{ fontSize: 13 }}>{wo.Asset?.name || '—'}</td>
                  <td style={{ fontSize: 13 }}>{wo.Asset?.category ? labelize(wo.Asset.category) : '—'}</td>
                  <td style={{ fontSize: 13 }}>{labelize(wo.type)}</td>
                  <td><span className={`badge bg-${PRIORITY_BADGE[wo.priority] || 'secondary'}`}>{labelize(wo.priority)}</span></td>
                  <td><span className={`badge bg-${STATUS_BADGE[wo.status] || 'secondary'}`}>{labelize(wo.status)}</span></td>
                  <td style={{ fontSize: 13 }}>{wo.dueAt ? new Date(wo.dueAt).toLocaleDateString() : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    {isQuickCompleteEligible(wo) && (
                      <button className="btn btn-sm btn-outline-success" onClick={() => setQuickCompleteTarget(wo)}>⚡ Quick Complete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <WorkOrderModal
          defaultAssetId={searchParams.get('assetId') || ''}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}

      {quickCompleteTarget && (
        <QuickCompleteWorkOrderModal
          workOrder={quickCompleteTarget}
          onSave={() => { setQuickCompleteTarget(null); load(); }}
          onClose={() => setQuickCompleteTarget(null)}
        />
      )}
      </div>
    </>
  );
}
